import React, { useState, useEffect, ReactNode } from 'react'
import _ from 'lodash'
import { Form } from 'semantic-ui-react'
import { match, P } from 'ts-pattern'
import { FilterSchema } from '@adc/blueprint'
import CheckboxField from '../../../formFields/checkboxField'
import StandardFieldLabel from '../../../formFields/formFieldWrapper/standardFieldLabel'

/**
 * This bad boy acts a lot like a SingleSelect (taking a options argument),
 * but it's for multiple selections.
 * In the case of web, it's a rendered as a bunch of checkboxes...
 */
type MultiSelectProps = FilterSchema.FilterLayoutProps<
  FilterSchema.FilterMultiSelectDefinition,
  FilterSchema.MultiSelectValueType
>

const toSnakeCase = (
  input:
    | FilterSchema.MultiSelectValueType
    | FilterSchema.MultiSelectControlValueType
) => {
  if (typeof input === 'string') return _.snakeCase(input)
  if (Array.isArray(input)) return _.snakeCase(input.join(' '))
  return 'none'
}

const isSubset = (a: string[], b: string[]) => {
  return _.difference(b, a).length === 0
}

const MultiSelect: React.FunctionComponent<MultiSelectProps> = ({
  definition,
  value,
  elmId,
  onChange,
}) => {
  // keep track locally which values are selected
  const [selectedValues, setSelectedValues] = useState(
    _.isNil(value) ? [] : value
  )

  useEffect(() => {
    setSelectedValues(_.isNil(value) ? [] : value)
  }, [value])

  /**
   * This is a little funky, but we need to handle the different types of values
   * that filterState throws at us (for now):
   *
   * value will be like ['a', 'b', 'c']
   * one optionValue (checkbox) will be like 'a' normally,
   * however SRP's filterState will be like ['a'] or ['a', 'b']
   */
  const onChangeSelection = (
    e: React.ChangeEvent<HTMLInputElement>,
    definition: FilterSchema.FilterMultiSelectDefinition,
    value: FilterSchema.MultiSelectControlValueType,
    checked: boolean
  ) => {
    let newValues: string[] | null = []
    if (Array.isArray(value)) {
      const currentValues: string[] = selectedValues
      if (checked) {
        // add the value to the selected values
        newValues = _.uniq<string>([...currentValues, ...value])
      } else {
        // remove the value from the selected values
        newValues = currentValues.filter(v => !value.includes(v))
      }
    } else if (typeof value === 'string') {
      if (checked) {
        // add the value to the selected values
        newValues = _.uniq<string>([...(selectedValues || []), value])
      } else {
        // remove the value from the selected values
        if (selectedValues?.length) {
          newValues = selectedValues.filter(v => v !== value)
        } else {
          newValues = selectedValues
        }
      }
    }

    setSelectedValues(newValues)
    onChange(definition.name, newValues)
    e.currentTarget.querySelector('input')?.focus()
  }

  const contains = (
    value: FilterSchema.MultiSelectValueType,
    optionValue: FilterSchema.MultiSelectControlValueType
  ) => {
    return (
      match(definition.controlValueType)
        // here, make sure the values in optionValue are all in value
        .with(
          P.when(
            v =>
              v === 'string[]' &&
              Array.isArray(value) &&
              Array.isArray(optionValue)
          ),
          () => isSubset(value as [], optionValue as string[])
        )
        .otherwise(() => {
          // here, just check if the value is in optionValue
          return _.isNil(value) ? false : value.includes(optionValue as string)
        })
    )
  }

  return (
    <Form style={{ marginTop: 6 }}>
      {definition.options.map((option, index) => (
        <CheckboxField
          key={index}
          compact={false}
          label={
            <StandardFieldLabel
              elmId={`${elmId}_${toSnakeCase(option.value)}_label`}
              label={option.label as NonNullable<Exclude<ReactNode, unknown>>}
              subLabel={(option.description as string) || ''}
            />
          }
          elmId={`${elmId}_${toSnakeCase(option.value)}`}
          name=''
          value={option.value}
          role='info'
          helpContent={option.tooltip} // TODO handle tooltips on checkbox
          checked={contains(value, option.value)}
          onChange={(
            e: React.ChangeEvent<HTMLInputElement>,
            data: Record<string, unknown>
          ) => {
            onChangeSelection(
              e,
              definition,
              data.value as FilterSchema.MultiSelectControlValueType,
              data.checked as boolean
            )
          }}
          valueAsArray={definition.controlValueType === 'string[]'}
          tabIndex={index}
        />
      ))}
    </Form>
  )
}

export default MultiSelect
